import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Card, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { useSnackbar } from 'notistack'
import { ReadinessChip } from '../../merchants/shared/StateChip'
import { C, SX, TYPE } from '../../merchants/shared/tokens'
import { errorText, fetchAllocation, saveAllocation } from '../ptlSetupApi'
import { fmtDate, num } from '../constants'
import { Banner } from '../components/SetupUi'
import { listText } from '../components/MerchantStatus'

/** One merchant's allocation couriers and the live PTL courier accounts (GET allocation). Null id = skip. */
export function useAllocation(merchantId) {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const reload = useCallback(() => {
    if (!merchantId) return
    setError('')
    fetchAllocation(merchantId).then(setData).catch((e) => setError(errorText(e, 'Could not load the couriers')))
  }, [merchantId])
  useEffect(() => { setData(null); reload() }, [reload])
  return { data, error, reload }
}

/** KPI cards of Step 3 (plan screen 4): eligible PTL couriers, with a rate card, selected, selected without a rate card. */
export function allocationKpis(data) {
  if (!data) return ['Eligible PTL couriers', 'With a rate card', 'Selected', 'Selected without a rate card'].map((label) => ({ key: label, label, value: '' }))
  const live = data.couriers.filter((c) => !c.notLive)
  const carded = live.filter((c) => c.rateCard)
  const noCard = data.couriers.filter((c) => c.selected && !c.rateCard)
  return [
    { key: 'eligible', label: 'Eligible PTL couriers', value: num(live.length), sub: 'Live PTL (B2B) courier accounts' },
    { key: 'carded', label: 'With a rate card', value: num(carded.length), tone: carded.length ? undefined : 'danger',
      sub: carded.length ? carded.map((c) => c.courier).join(', ') : 'Upload one in Step 1' },
    { key: 'selected', label: 'Selected', value: num(data.selected.length), tone: data.selected.length ? undefined : 'warn',
      sub: data.selected.length ? 'Allocation couriers' : 'None: cheapest carded courier is used' },
    { key: 'nocard', label: 'Selected without a rate card', value: num(noCard.length), tone: noCard.length ? 'warn' : 'ok',
      sub: noCard.length ? 'Can be offered, never priced' : 'Every pick has a price' },
  ]
}

const VIEWS = [
  { key: 'carded', label: 'With a rate card', test: (c) => c.rateCard },
  { key: 'selected', label: 'Selected', test: (c, picked) => picked.has(c.courier) },
  { key: 'all', label: 'All PTL couriers', test: () => true },
]

/**
 * Step 3 Allocation body (plan screen 4): pick the merchant's PTL allocation couriers from the live PTL courier
 * accounts, the contract's couriers only. Draft on screen → Save → confirm dialog (added / removed, couriers without a
 * rate card need an acknowledgement) → save.
 */
export default function AllocationStep({ merchant, data, error, reload, onChanged, onUnsaved }) {
  const { enqueueSnackbar } = useSnackbar()
  const [picked, setPicked] = useState(new Set())
  const [view, setView] = useState('carded')
  const [search, setSearch] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!data) return
    setPicked(new Set(data.selected))
    // Open on what matters: the carded couriers, unless the merchant has none yet.
    setView(data.couriers.some((c) => c.rateCard) ? 'carded' : 'all')
  }, [data])

  const byId = useMemo(() => Object.fromEntries((data?.couriers || []).map((c) => [c.courier, c])), [data])
  const saved = useMemo(() => new Set(data?.selected || []), [data])
  const added = [...picked].filter((c) => !saved.has(c))
  const removed = [...saved].filter((c) => !picked.has(c))
  const dirty = added.length > 0 || removed.length > 0
  // Unsaved picks hold Finish, and leaving the step by Back, the stepper or the top bar asks first (PtlOnboardingStep).
  useEffect(() => { if (onUnsaved) onUnsaved(dirty) }, [dirty, onUnsaved])
  const noCard = [...picked].filter((c) => !byId[c]?.rateCard)
  // The server refuses couriers that are no longer live PTL accounts, so they must go before a save.
  const notLive = [...picked].filter((c) => byId[c]?.notLive)
  // Step 2 put the merchant on one courier's TAT. PTL allocation gives a pickup to the courier that costs Prozo least
  // among the picks (merchant rate card not checked), or with none picked among every serviceable PTL courier. Warn
  // when any courier other than the TAT courier can win.
  const tatCp = merchant.tatCourier
  const otherPicks = tatCp ? [...picked].filter((c) => c !== tatCp && !byId[c]?.notLive) : []
  const tatWarn = !!tatCp && (picked.size === 0 || otherPicks.length > 0)

  if (error) return <Banner tone='danger' action={<Button size='small' onClick={reload} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>
  if (!data) return <Typography role='status' sx={{ ...TYPE.rowHelp, py: 4, textAlign: 'center' }}>Loading couriers…</Typography>

  const v = VIEWS.find((x) => x.key === view)
  const q = search.trim().toLowerCase()
  const rows = data.couriers
    .filter((c) => v.test(c, picked) && (!q || c.courier.toLowerCase().includes(q) || (c.accountCode || '').toLowerCase().includes(q)))
    .sort((a, b) => Number(picked.has(b.courier)) - Number(picked.has(a.courier)) || Number(b.rateCard) - Number(a.rateCard) || a.courier.localeCompare(b.courier))
  const toggle = (cp) => { const next = new Set(picked); if (next.has(cp)) next.delete(cp); else next.add(cp); setPicked(next) }

  const save = async () => {
    setBusy(true)
    try {
      // Saved order: what was there first, then the new picks.
      const list = [...data.selected.filter((c) => picked.has(c)), ...added]
      await saveAllocation(merchant.id, list, noCard.length > 0 && acknowledged)
      enqueueSnackbar('Allocation couriers saved. The change is in its History.', { variant: 'success' })
      setConfirmOpen(false)
      setAcknowledged(false)
      reload()
      onChanged()
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not save the couriers'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box>
      <Banner tone='info'>
        Select only couriers named in the commercial contract. If the contract does not name them (e.g. blended rate card), ask the PTL BU head by
        email which couriers to map.
      </Banner>

      {/* The courier TAT only matches the EDD when that courier carries the shipments. */}
      {tatWarn && (
        <Banner tone='warn'>
          {merchant.name} is on the {tatCp} TAT (Step 2), but its shipments can also go to
          {picked.size ? ` ${listText(otherPicks)}` : ' any serviceable PTL courier (with none selected, the one that costs Prozo least gets the pickup)'}.
          {' '}Those shipments get the {tatCp} TAT too. Select only {tatCp}{byId[tatCp]?.rateCard ? '' : ' and add its rate card in Step 1'}, or put
          the merchant back on the standard TAT in Step 2.
        </Banner>
      )}

      {notLive.length > 0 && (
        <Banner tone='warn' action={
          <Button size='small' onClick={() => setPicked(new Set([...picked].filter((c) => !notLive.includes(c))))} sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Remove them
          </Button>
        }>
          {notLive.join(', ')} {notLive.length === 1 ? 'is' : 'are'} no longer {notLive.length === 1 ? 'a live PTL courier account' : 'live PTL courier accounts'} (deleted, or not B2B).
          Remove {notLive.length === 1 ? 'it' : 'them'} before saving.
        </Banner>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <TextField size='small' placeholder='Search courier' value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: '100%', sm: 260 } }} inputProps={{ 'aria-label': 'Search courier' }}
          InputProps={{
            startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 18, color: C.inkGhost }} /></InputAdornment>,
            endAdornment: search ? (
              <InputAdornment position='end'>
                <IconButton size='small' aria-label='Clear search' edge='end' onClick={() => setSearch('')}><ClearIcon fontSize='small' /></IconButton>
              </InputAdornment>
            ) : null,
          }} />
        <Box role='group' aria-label='Show couriers' sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {VIEWS.map((x) => {
            const on = view === x.key
            return (
              <Button key={x.key} size='small' variant='outlined' aria-pressed={on} onClick={() => setView(x.key)}
                sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32, ...(on && { color: C.accent, borderColor: C.accentBorder, bgcolor: C.accentSoft }) }}>
                {x.label} · {num(data.couriers.filter((c) => x.test(c, picked)).length)}
              </Button>
            )
          })}
        </Box>
      </Box>

      <Card elevation={0} sx={{ ...SX.cardFlush, overflowX: 'auto' }}>
        <Table size='small' sx={{ minWidth: 520 }} aria-label='PTL couriers'>
          <TableHead>
            <TableRow>
              {['', 'Courier', 'Rate card for this merchant', ''].map((h, i) => (
                <TableCell key={i} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => {
              const on = picked.has(c.courier)
              const id = `ptl-alloc-${c.courier}`
              return (
                <TableRow key={c.courier} hover onClick={() => toggle(c.courier)} sx={{ cursor: 'pointer', '& td': { borderColor: C.lineSoft, py: 0.5 } }}>
                  <TableCell padding='checkbox'>
                    <Checkbox checked={on} onClick={(e) => e.stopPropagation()} onChange={() => toggle(c.courier)} inputProps={{ 'aria-labelledby': id }} />
                  </TableCell>
                  <TableCell>
                    <Typography id={id} sx={TYPE.rowLabel}>{c.courier}</Typography>
                    {c.accountCode && c.accountCode !== c.courier && <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{c.accountCode}</Typography>}
                  </TableCell>
                  <TableCell><ReadinessChip tone={c.rateCard ? 'done' : on ? 'pending' : 'none'} label={c.rateCard ? 'Rate card' : 'No rate card'} /></TableCell>
                  <TableCell align='right'>
                    {c.notLive && <ReadinessChip tone='blocked' label='Not a live PTL courier' hint='Deleted, or not a B2B account. Remove it from the list.' />}
                  </TableCell>
                </TableRow>
              )
            })}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={4} sx={{ ...TYPE.rowHelp, textAlign: 'center', py: 3 }}>
                {q ? 'No courier matches the search.' : view === 'carded' ? 'No courier has a rate card for this merchant yet. Upload one in Step 1.' : 'No courier in this view.'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 2 }}>
        <Typography role='status' sx={{ ...TYPE.rowHelp, color: C.inkBody, flex: '1 1 320px' }}>
          {picked.size
            ? `${num(picked.size)} courier${picked.size === 1 ? '' : 's'} selected${noCard.length ? `, ${num(noCard.length)} without a rate card` : ''}.`
            : 'None selected: every PTL pickup goes to the cheapest courier with a rate card.'}
          {dirty ? ' Not saved yet.' : ''}
          {notLive.length ? ` Remove ${num(notLive.length)} courier${notLive.length === 1 ? '' : 's'} that ${notLive.length === 1 ? 'is' : 'are'} not live to save.` : ''}
          {data.latest && !dirty ? ` Last change ${fmtDate(data.latest.createdAt)}${data.latest.createdBy ? ` by ${data.latest.createdBy}` : ''}.` : ''}
        </Typography>
        <Button disabled={!dirty || busy} onClick={() => setPicked(new Set(data.selected))} sx={{ textTransform: 'none', fontWeight: 600, color: C.inkMuted }}>Undo changes</Button>
        <Button variant='contained' disabled={!dirty || busy || notLive.length > 0} onClick={() => { setAcknowledged(false); setConfirmOpen(true) }} sx={{ ...SX.cta, minHeight: 36 }}>
          Save couriers
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Save the allocation couriers of {merchant.name}?</DialogTitle>
        <DialogContent>
          {added.length > 0 && <Typography sx={{ ...TYPE.rowHelp, color: C.inkBody, mb: 1 }}><b>Added:</b> {added.join(', ')}</Typography>}
          {removed.length > 0 && <Typography sx={{ ...TYPE.rowHelp, color: C.inkBody, mb: 1 }}><b>Removed:</b> {removed.join(', ')}</Typography>}
          <Typography sx={{ ...TYPE.rowHelp, color: C.inkBody, mb: noCard.length ? 1.5 : 0 }}>
            {picked.size
              ? `New PTL pickups of this merchant go to the cheapest of these ${num(picked.size)} courier${picked.size === 1 ? '' : 's'}.`
              : 'With no courier selected, new PTL pickups go to the cheapest courier with a rate card.'}
          </Typography>
          {noCard.length > 0 && (
            <>
              <Banner tone='warn'>No rate card for {noCard.join(', ')} on this merchant: {noCard.length === 1 ? 'it' : 'they'} can be offered but never priced.</Banner>
              <FormControlLabel control={<Checkbox size='small' checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 13, color: C.inkBody }}>Save them anyway</Typography>} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
          <LoadingButton variant='contained' loading={busy} disabled={noCard.length > 0 && !acknowledged} onClick={save} sx={SX.cta}>Save couriers</LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
